import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { RTCSessionEvent } from 'jssip/lib/UA';
import { api } from '../../lib/api/client.js';
import type { BrowserPhone } from '@arcnet/contracts';
import { useMessages, extensionOf } from './messages.js';

/**
 * The workspace's phone: JsSIP over WSS to the edge (docs/36 §8.1).
 *
 * One hook, because a phone is one thing with one state: it is signed in or it is not, there is a
 * call or there is not. Splitting registration from the call would let the two disagree, and a
 * phone that thinks it is registered while the edge has forgotten it is the failure people
 * describe as "it rang once and then never again".
 *
 * The credential is the session's, not the seat's (API browserphone.ts): minted when the phone is
 * switched on and revoked when it is switched off, so nothing in this page outlives the tab.
 * Sign-out therefore has to happen on the way out as well -- the unload handler below -- or a
 * closed tab would leave a registration nobody can see answering calls in an empty room.
 *
 * Added 3 October 2026 (owner: "one tab owning the phone, recovering from network changes,
 * microphone and speaker choice, attended transfer"):
 *  - ONE TAB OWNS IT. A Web Lock per extension: the tab that signs in holds it; another tab sees
 *    "open in another tab" and can take it over. Two tabs registered would ring twice and fight
 *    over the microphone.
 *  - NETWORK CHANGES. The socket reconnects and re-registers by itself (JsSIP, sooner than its
 *    default); a call's audio path is restarted (ICE restart) when the network changes or ICE
 *    drops, and the call says so while it is lost rather than going silent.
 *  - DEVICES. Microphone and speaker, kept in the browser; changed mid-call without a re-INVITE
 *    (the sender's track is replaced, the speaker is the audio element's sink).
 *  - TALKING FIRST. A second, consultation call while the first is held; then hand the caller over
 *    (REFER with Replaces), go back, swap between them, or join all three (mixed here, in the
 *    browser: each side hears this microphone and the other side).
 */

export type PhoneState = 'off' | 'connecting' | 'ready' | 'reconnecting' | 'failed';
export type CallState = 'idle' | 'ringing' | 'calling' | 'answered' | 'held';
/** Whether the call's audio is flowing: 'reconnecting' while the path is being restored. */
export type AudioPath = 'ok' | 'reconnecting' | 'lost';

export interface PhoneCall {
  /** Who is on the other end, as the network gave them. */
  number: string;
  name: string | null;
  inbound: boolean;
  state: CallState;
  /** When it was answered, for the timer. Null while it is still ringing. */
  answeredAt: number | null;
  muted: boolean;
  audio: AudioPath;
}

interface Options {
  tenantId: string;
  extensionId: string;
  /** Called when a call ends, so the workspace can refresh what it shows. */
  onEnded?: () => void;
}

/** How the last call ended, for a campaign's outcome (docs/36 D1b). */
export interface CallEnd {
  at: number;
  /** It was answered at some point. */
  answered: boolean;
  /** JsSIP's cause when it did not connect: Busy, No Answer, Unavailable, Rejected, Canceled... */
  cause: string | null;
  /** The campaign attempt it was placed for, if any. */
  attemptId: string | null;
}

export interface AudioDevices {
  inputs: Array<{ id: string; label: string }>;
  outputs: Array<{ id: string; label: string }>;
  /** Whether the browser has shown the names yet: it does after the microphone is allowed. */
  named: boolean;
  /** Whether a speaker can be chosen at all (setSinkId: Chrome and Edge, not Safari or Firefox). */
  canChooseSpeaker: boolean;
}

export interface BrowserPhoneApi {
  info: BrowserPhone | undefined;
  state: PhoneState;
  error: string | null;
  call: PhoneCall | null;
  lastEnd: CallEnd | null;
  /** Another tab holds this extension's phone. */
  elsewhere: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Asks the tab that holds the phone to let go, and signs in here. */
  takeOver: () => Promise<void>;
  /** attemptId: a preview campaign's call, named to the switch so its record joins the attempt. */
  dial: (number: string, opts?: { attemptId?: string; callerId?: string | null }) => void;
  answer: () => void;
  hangUp: () => void;
  hold: (on: boolean) => void;
  mute: (on: boolean) => void;
  sendDtmf: (digit: string) => void;
  /** Blind transfer: the call leaves and this phone is free. */
  transfer: (target: string) => void;

  /** The consultation call, while one is up; `call` is then the held caller. */
  consult: PhoneCall | null;
  /** All three joined, after merge. */
  conference: boolean;
  consultStart: (target: string) => void;
  /** Hands the held caller to the person consulted, and frees this phone. */
  consultComplete: () => void;
  /** Ends the consultation and goes back to the caller. */
  consultCancel: () => void;
  /** Talks to the other one: the one talking now is held. */
  consultSwap: () => void;
  /** Joins all three. */
  merge: () => void;

  devices: AudioDevices;
  micId: string | null;
  speakerId: string | null;
  setMic: (id: string | null) => void;
  setSpeaker: (id: string | null) => void;
  /** Asks for the microphone once, so the browser names the devices. */
  allowDevices: () => Promise<void>;
  /** The microphone track in use on the call, for a level meter. */
  micTrack: () => MediaStreamTrack | null;
  /** A chat message to a colleague's extension; the result lands on it in the messages store. */
  sendMessage: (extension: string, text: string) => void;
  /** The other side's audio on the call, for the same. */
  remoteTrack: () => MediaStreamTrack | null;
}

/** A few random letters and digits, for the made-up host JsSIP would use. */
const rand = (n: number): string => Array.from({ length: n }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]).join('');

const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string | null): void => {
  try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* private window */ }
};

/** The remote side's audio of a session, as a stream. */
function remoteStream(s: RTCSession): MediaStream {
  const stream = new MediaStream();
  s.connection?.getReceivers().forEach((r) => { if (r.track) stream.addTrack(r.track); });
  return stream;
}
const audioSender = (s: RTCSession | null): RTCRtpSender | null =>
  s?.connection?.getSenders().find((x) => x.track?.kind === 'audio' || x.track === null) ?? null;

export function useBrowserPhone({ tenantId, extensionId, onEnded }: Options): BrowserPhoneApi {
  const [info, setInfo] = useState<BrowserPhone | undefined>(undefined);
  const [state, setState] = useState<PhoneState>('off');
  const [error, setError] = useState<string | null>(null);
  const [call, setCall] = useState<PhoneCall | null>(null);
  const [consult, setConsult] = useState<PhoneCall | null>(null);
  const [conference, setConference] = useState(false);
  const [elsewhere, setElsewhere] = useState(false);

  const ua = useRef<JsSIP.UA | null>(null);
  const session = useRef<RTCSession | null>(null);
  const consultSession = useRef<RTCSession | null>(null);
  /** The next outgoing session is the consultation, not a new call. */
  const nextIsConsult = useRef(false);
  const attemptRef = useRef<string | null>(null);
  const [lastEnd, setLastEnd] = useState<CallEnd | null>(null);
  /** One element per party, made once: a new element per call loses the autoplay grant. */
  const audio = useRef<HTMLAudioElement | null>(null);
  const audio2 = useRef<HTMLAudioElement | null>(null);
  const mixer = useRef<{ ctx: AudioContext; gain: GainNode } | null>(null);
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/browser-phone`;
  const lockName = `arc-phone-${extensionId}`;
  const release = useRef<(() => void) | null>(null);
  const lastSocketError = useRef<string | null>(null);
  const chan = useRef<BroadcastChannel | null>(null);

  // ------------------------------------------------------------------ devices
  const [micId, setMicId] = useState<string | null>(() => read('arc.phone.mic'));
  const [speakerId, setSpeakerId] = useState<string | null>(() => read('arc.phone.speaker'));
  const canChooseSpeaker = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  const [devices, setDevices] = useState<AudioDevices>({ inputs: [], outputs: [], named: false, canChooseSpeaker });

  const listDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const all = await navigator.mediaDevices.enumerateDevices();
    const pick = (kind: MediaDeviceKind) => all.filter((d) => d.kind === kind && d.deviceId !== 'communications')
      .map((d, i) => ({ id: d.deviceId, label: d.label || (kind === 'audioinput' ? `Microphone ${i + 1}` : `Speaker ${i + 1}`) }));
    setDevices({ inputs: pick('audioinput'), outputs: pick('audiooutput'), named: all.some((d) => d.label), canChooseSpeaker });
  }, [canChooseSpeaker]);
  useEffect(() => {
    void listDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', listDevices);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', listDevices);
  }, [listDevices]);

  const micConstraint = useCallback((): MediaTrackConstraints | boolean => (
    micId ? { deviceId: { exact: micId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true } : true
  ), [micId]);

  if (audio.current === null && typeof document !== 'undefined') {
    audio.current = document.createElement('audio');
    audio.current.autoplay = true;
    audio2.current = document.createElement('audio');
    audio2.current.autoplay = true;
  }
  useEffect(() => {
    for (const el of [audio.current, audio2.current]) {
      const e = el as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
      if (e?.setSinkId) void e.setSinkId(speakerId ?? '').catch(() => undefined);
    }
  }, [speakerId]);

  useEffect(() => {
    let live = true;
    api.get<BrowserPhone>(base).then((r) => { if (live) setInfo(r); }).catch(() => undefined);
    return () => { live = false; };
  }, [base]);

  // --------------------------------------------------------------- the calls
  /** The far end's track, played on its own element. */
  const attachMedia = useCallback((s: RTCSession, el: HTMLAudioElement | null) => {
    const pc = s.connection;
    const play = (): void => {
      if (el) { el.srcObject = remoteStream(s); void el.play().catch(() => undefined); }
    };
    pc.addEventListener('track', play);
    play();
  }, []);

  /**
   * The audio path, watched: ICE drops when the network changes under a call. 'disconnected' often
   * heals by itself, so it waits a moment before restarting; 'failed' restarts at once, and a
   * restart that does not bring it back within 15 seconds says the audio is lost.
   */
  const watchIce = useCallback((s: RTCSession, set: (fn: (c: PhoneCall | null) => PhoneCall | null) => void) => {
    const pc = s.connection;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let giveUp: ReturnType<typeof setTimeout> | null = null;
    const restart = (): void => {
      if (s.isEnded()) return;
      s.renegotiate({ rtcOfferConstraints: { iceRestart: true } } as never);
      if (giveUp) clearTimeout(giveUp);
      giveUp = setTimeout(() => {
        if (pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
          set((c) => c && { ...c, audio: 'lost' });
        }
      }, 15_000);
    };
    pc.addEventListener('iceconnectionstatechange', () => {
      const st = pc.iceConnectionState;
      if (timer) { clearTimeout(timer); timer = null; }
      if (st === 'connected' || st === 'completed') { if (giveUp) clearTimeout(giveUp); set((c) => c && { ...c, audio: 'ok' }); }
      else if (st === 'disconnected') { set((c) => c && { ...c, audio: 'reconnecting' }); timer = setTimeout(restart, 3_000); }
      else if (st === 'failed') { set((c) => c && { ...c, audio: 'reconnecting' }); restart(); }
    });
    return restart;
  }, []);
  const restarts = useRef(new Map<RTCSession, () => void>());

  const unmix = useCallback(() => {
    const m = mixer.current;
    if (!m) return;
    mixer.current = null;
    setConference(false);
    // Whoever is still on hears this microphone straight again.
    navigator.mediaDevices.getUserMedia({ audio: micConstraint() }).then((stream) => {
      const t = stream.getAudioTracks()[0]!;
      for (const s of [session.current, consultSession.current]) void audioSender(s)?.replaceTrack(t);
    }).catch(() => undefined);
    void m.ctx.close();
  }, [micConstraint]);

  const blank = (s: RTCSession, inbound: boolean): PhoneCall => ({
    number: s.remote_identity.uri.user ?? '', name: s.remote_identity.display_name || null,
    inbound, state: inbound ? 'ringing' : 'calling', answeredAt: null, muted: false, audio: 'ok',
  });

  const wire = useCallback((s: RTCSession, inbound: boolean) => {
    session.current = s;
    setCall(blank(s, inbound));
    s.on('accepted', () => setCall((c) => c && { ...c, state: 'answered', answeredAt: Date.now() }));
    s.on('confirmed', () => {
      attachMedia(s, audio.current);
      restarts.current.set(s, watchIce(s, setCall));
      setCall((c) => c && { ...c, state: 'answered', answeredAt: c.answeredAt ?? Date.now() });
    });
    s.on('hold', () => setCall((c) => c && { ...c, state: 'held' }));
    s.on('unhold', () => setCall((c) => c && { ...c, state: 'answered' }));
    let answered = false;
    s.on('confirmed', () => { answered = true; });
    const gone = (e?: { cause?: string }): void => {
      restarts.current.delete(s);
      if (session.current !== s) return;
      unmix();
      // The caller hung up during a consultation: the consultation becomes the call.
      const other = consultSession.current;
      if (other && !other.isEnded()) {
        session.current = other;
        consultSession.current = null;
        setConsult(null);
        setCall((_) => null);
        setCall(() => ({ ...blank(other, false), state: other.isOnHold().local ? 'held' : 'answered', answeredAt: Date.now() }));
        if (audio.current && audio2.current) audio.current.srcObject = audio2.current.srcObject;
        return;
      }
      session.current = null;
      setCall(null);
      setLastEnd({ at: Date.now(), answered, cause: answered ? null : (e?.cause ?? null), attemptId: attemptRef.current });
      attemptRef.current = null;
      onEnded?.();
    };
    s.on('ended', gone);
    s.on('failed', gone);
  }, [attachMedia, onEnded, unmix, watchIce]);

  const wireConsult = useCallback((s: RTCSession) => {
    consultSession.current = s;
    setConsult(blank(s, false));
    s.on('accepted', () => setConsult((c) => c && { ...c, state: 'answered', answeredAt: Date.now() }));
    s.on('confirmed', () => {
      attachMedia(s, audio2.current);
      restarts.current.set(s, watchIce(s, setConsult));
      setConsult((c) => c && { ...c, state: 'answered', answeredAt: c.answeredAt ?? Date.now() });
    });
    s.on('hold', () => setConsult((c) => c && { ...c, state: 'held' }));
    s.on('unhold', () => setConsult((c) => c && { ...c, state: 'answered' }));
    const gone = (): void => {
      restarts.current.delete(s);
      if (consultSession.current !== s) return;
      consultSession.current = null;
      setConsult(null);
      unmix();
      if (audio2.current) audio2.current.srcObject = null;
      // Back to the caller, who was waiting on hold.
      const main = session.current;
      if (main && main.isOnHold().local) main.unhold();
    };
    s.on('ended', gone);
    s.on('failed', gone);
  }, [attachMedia, unmix, watchIce]);

  // --------------------------------------------------------- one tab owns it
  /** Takes this extension's lock if nobody holds it; held until the phone is switched off here. */
  const acquire = useCallback((wait: boolean): Promise<boolean> => new Promise((resolve) => {
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (!locks) { resolve(true); return; }
    void locks.request(lockName, wait ? {} : { ifAvailable: true }, (lock) => {
      if (!lock) { resolve(false); return undefined; }
      resolve(true);
      return new Promise<void>((done) => { release.current = done; });
    });
  }), [lockName]);

  const signOutRef = useRef<() => Promise<void>>(async () => undefined);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const c = new BroadcastChannel(lockName);
    chan.current = c;
    c.onmessage = (ev: MessageEvent<string>) => {
      const owner = !!ua.current;
      if (ev.data === 'who' && owner) c.postMessage('on');
      else if (ev.data === 'on' && !owner) setElsewhere(true);
      else if (ev.data === 'off' && !owner) setElsewhere(false);
      // Asked to let go: only between calls. A live call is not taken from under somebody.
      else if (ev.data === 'take' && owner) {
        if (session.current) c.postMessage('busy');
        else void signOutRef.current();
      } else if (ev.data === 'busy' && !owner) setError('The other tab is on a call. Take the phone over when it ends.');
    };
    c.postMessage('who');
    // A tab that reloaded asks the locks too: the lock is the truth, the messages are courtesy.
    void (navigator as Navigator & { locks?: LockManager }).locks?.query().then((q) => {
      if (!ua.current && q.held?.some((l) => l.name === lockName)) setElsewhere(true);
    });
    return () => { c.close(); chan.current = null; };
  }, [lockName]);

  // ---------------------------------------------------------- sign in and out
  const start = useCallback(async () => {
    const r = await api.post<BrowserPhone & { password: string }>(base, {});
    setInfo({ ...r, signed_in: true });
    const socket = new JsSIP.WebSocketInterface(r.ws_url!);
    const agent = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${r.username}@${r.realm}`,
      authorization_user: r.username,
      password: r.password,
      display_name: r.display_name,
      // Short enough that a laptop waking from sleep is back on the edge quickly, long enough
      // not to be a heartbeat: the edge counts registrations per source like any other phone.
      register_expires: 300,
      session_timers: false,
      // Back on the edge within seconds of the network returning, not JsSIP's default 30.
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 10,
      /**
       * JsSIP's own made-up contact, plus ;arc-messaging=1 IN the address: that is how the edge
       * knows this registration shows messages (route[MESSAGE_ROUTER]). A header parameter would
       * not do -- the switch stores the address only, and the first test's messages found nobody.
       */
      contact_uri: `sip:${rand(8)}@${rand(12)}.invalid;transport=ws;arc-messaging=1`,
    });
    // A colleague's message. Read receipts (message/imdn+xml) are not chat and are not shown.
    agent.on('newMessage', (e: { originator: string; request: { body?: string; from: { uri: { user: string } }; getHeader: (n: string) => string | undefined } }) => {
      if (e.originator !== 'remote') return;
      const type = e.request.getHeader('Content-Type') ?? 'text/plain';
      if (!/^text\/plain/i.test(type) || !e.request.body) return;
      useMessages.getState().receive(extensionOf(e.request.from.uri.user), e.request.body);
    });
    agent.on('registered', () => { setState('ready'); setError(null); });
    agent.on('unregistered', () => setState((s) => (s === 'reconnecting' ? s : 'off')));
    /**
     * A fresh credential reaches the switch a moment after it is minted: the node is told at once
     * but pulls in a second or two, and a browser registers in less (3 October 2026: refused at
     * 17:05:34 for a credential minted at 17:05:33.9). So a refusal in the first half-minute is
     * tried again with the same password, sooner then later, before it is called a failure.
     */
    const mintedAt = Date.now();
    let retries = 0;
    agent.on('registrationFailed', (e) => {
      if (Date.now() - mintedAt < 30_000 && retries < 4) {
        const wait = 1_000 * 2 ** retries;
        retries++;
        setTimeout(() => { if (ua.current === agent) agent.register(); }, wait);
        return;
      }
      setState('failed');
      setError(`The edge refused this phone (${e.cause}).`);
    });
    // A dropped socket is a network moment, not a failure: JsSIP reconnects and registers again.
    // While still connecting, the cause is kept for the message the timeout gives.
    agent.on('disconnected', (e: { error?: boolean; code?: number; reason?: string }) => {
      lastSocketError.current = e?.code ? `socket closed ${e.code}${e.reason ? `: ${e.reason}` : ''}` : 'socket closed';
      setState((s) => (s === 'off' || s === 'connecting' ? s : 'reconnecting'));
    });
    agent.on('newRTCSession', ({ session: s, originator }: RTCSessionEvent) => {
      if (originator === 'local' && nextIsConsult.current) {
        nextIsConsult.current = false;
        wireConsult(s);
        return;
      }
      // One call at a time: a second arriving while one is up is refused rather than
      // silently taking over the audio device.
      if (session.current) { s.terminate({ status_code: 486 }); return; }
      wire(s, originator === 'remote');
    });
    agent.start();
    ua.current = agent;
    chan.current?.postMessage('on');
    setElsewhere(false);
  }, [base, wire, wireConsult]);

  /**
   * One sign-in at a time (3 October 2026): Log in starts the phone, and so does the status bar's
   * "logged in, so the phone should be on"; both at once found the lock taken by the other.
   */
  const signingIn = useRef(false);
  const signIn = useCallback(async () => {
    if (signingIn.current || ua.current) return;
    signingIn.current = true;
    setError(null);
    try {
      if (!(await acquire(false))) { setElsewhere(true); return; }
      setState('connecting');
      try {
        await start();
        write(`arc.phone.on.${extensionId}`, '1');
      } catch (e) {
        release.current?.(); release.current = null;
        setState('failed');
        setError(e instanceof Error ? e.message : 'The phone could not sign in.');
      }
    } finally { signingIn.current = false; }
  }, [acquire, start, extensionId]);

  /**
   * Connecting does not last for ever: the edge answers a browser in under a second, so twenty
   * seconds without a registration is a failure, said as one, with whatever the socket said.
   */
  useEffect(() => {
    if (state !== 'connecting') return;
    const t = setTimeout(() => {
      setState((s) => (s === 'connecting' ? 'failed' : s));
      setError(`The phone could not reach the edge${lastSocketError.current ? ` (${lastSocketError.current})` : ''}. Try again; if it keeps failing, this network may block port 7443.`);
    }, 20_000);
    return () => clearTimeout(t);
  }, [state]);

  const signOut = useCallback(async () => {
    consultSession.current?.terminate();
    session.current?.terminate();
    session.current = null;
    consultSession.current = null;
    ua.current?.stop();
    ua.current = null;
    setState('off');
    setCall(null);
    setConsult(null);
    write(`arc.phone.on.${extensionId}`, null);
    release.current?.(); release.current = null;
    chan.current?.postMessage('off');
    await api.delete(base).catch(() => undefined);
    setInfo((i) => i && { ...i, signed_in: false });
  }, [base, extensionId]);
  signOutRef.current = async () => { await signOut(); };

  const takeOver = useCallback(async () => {
    setError(null);
    chan.current?.postMessage('take');
    setState('connecting');
    // Waits for the other tab to let go; it does at once between calls.
    const got = await Promise.race([acquire(true), new Promise<boolean>((r) => setTimeout(() => r(false), 6_000))]);
    if (!got) { setState('off'); setError((e) => e ?? 'The other tab did not let go of the phone.'); return; }
    try { await start(); write(`arc.phone.on.${extensionId}`, '1'); } catch (e) {
      release.current?.(); release.current = null;
      setState('failed'); setError(e instanceof Error ? e.message : 'The phone could not sign in.');
    }
  }, [acquire, start, extensionId]);

  /**
   * On again after a reload, if it was on: somebody who refreshes the page mid-shift should not
   * have to remember the phone. Only where no other tab holds it.
   */
  useEffect(() => {
    if (read(`arc.phone.on.${extensionId}`) === '1') void signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionId]);

  /**
   * A closed tab gives the credential back.
   *
   * Best effort, deliberately: the request is sent on the way out and the browser may cut it off,
   * so the credential is ALSO replaced by the next sign-in and dropped when the seat's password is
   * reissued. Nothing here is the only thing standing between a closed tab and a live phone --
   * unregistering is, and that happens first. The "was on" mark is kept, so a reload comes back on.
   */
  useEffect(() => {
    const leave = (): void => {
      if (!ua.current) return;
      ua.current.stop();
      ua.current = null;
      release.current?.(); release.current = null;
      chan.current?.postMessage('off');
      void api.delete(base).catch(() => undefined);
    };
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [base]);

  /**
   * Leaving mid-call asks first (owner, 3 October 2026): a refresh or a closed tab ends the call,
   * and a press of F5 should not hang up on a customer. Moving between portal pages does not end
   * it (the phone is the portal's), so only the browser's own leaving is guarded.
   */
  const live = call !== null || consult !== null;
  useEffect(() => {
    if (!live) return;
    const ask = (e: BeforeUnloadEvent): void => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [live]);

  /**
   * The network changed under the phone: a new Wi-Fi, a VPN, a cable. The socket recovers on its
   * own; a call's audio path does not, because its addresses are the old network's, so it is
   * restarted. Offline is said straight away rather than discovered on the next call.
   */
  useEffect(() => {
    const changed = (): void => { for (const r of restarts.current.values()) r(); };
    const offline = (): void => { if (ua.current) setState('reconnecting'); };
    window.addEventListener('online', changed);
    window.addEventListener('offline', offline);
    const conn = (navigator as Navigator & { connection?: EventTarget }).connection;
    conn?.addEventListener?.('change', changed);
    return () => {
      window.removeEventListener('online', changed);
      window.removeEventListener('offline', offline);
      conn?.removeEventListener?.('change', changed);
    };
  }, []);

  // ---------------------------------------------------------------- controls
  const callOpts = useCallback(() => ({
    mediaConstraints: { audio: micConstraint(), video: false },
    // The microphone is asked for HERE, on a call, not when the page loads: a permission
    // prompt on arrival is how people end up refusing it for good.
    pcConfig: { rtcpMuxPolicy: 'require' as const },
  }), [micConstraint]);

  const dial = useCallback((number: string, opts?: { attemptId?: string; callerId?: string | null }) => {
    const agent = ua.current;
    const target = number.trim();
    if (!agent || !target || session.current) return;
    attemptRef.current = opts?.attemptId ?? null;
    agent.call(`sip:${target}@${info?.realm ?? ''}`, {
      // Only a uuid: the dialplan takes nothing else from it and drops it before the carrier.
      // A campaign call names its attempt, and the number it shows (0137); the switch takes the
      // number only when it is one of the account's own.
      ...(opts?.attemptId && /^[0-9a-f-]{36}$/.test(opts.attemptId) ? { extraHeaders: [
        `X-Arc-Attempt: ${opts.attemptId}`,
        ...(opts.callerId && /^\+\d{6,15}$/.test(opts.callerId) ? [`X-Arc-Cli: ${opts.callerId}`] : []),
      ] } : {}),
      ...callOpts(),
    });
    void listDevices();
  }, [info?.realm, callOpts, listDevices]);

  const answer = useCallback(() => {
    session.current?.answer(callOpts());
    void listDevices();
  }, [callOpts, listDevices]);
  const hangUp = useCallback(() => {
    // In a three-way call, hanging up ends it for everybody here.
    consultSession.current?.terminate();
    session.current?.terminate();
  }, []);
  const hold = useCallback((on: boolean) => {
    if (on) session.current?.hold(); else session.current?.unhold();
  }, []);
  const mute = useCallback((on: boolean) => {
    if (mixer.current) mixer.current.gain.gain.value = on ? 0 : 1;
    for (const s of [session.current, consultSession.current]) {
      if (!s) continue;
      if (on) s.mute({ audio: true }); else s.unmute({ audio: true });
    }
    setCall((c) => c && { ...c, muted: on });
  }, []);
  const sendDtmf = useCallback((digit: string) => {
    (consultSession.current && !consultSession.current.isOnHold().local ? consultSession.current : session.current)?.sendDTMF(digit);
  }, []);
  const transfer = useCallback((target: string) => {
    if (!target.trim()) return;
    session.current?.refer(`sip:${target.trim()}@${info?.realm ?? ''}`);
  }, [info?.realm]);

  // ------------------------------------------------------- attended transfer
  const consultStart = useCallback((target: string) => {
    const agent = ua.current;
    const main = session.current;
    const to = target.trim();
    if (!agent || !main || !to || consultSession.current) return;
    if (!main.isOnHold().local) main.hold();
    nextIsConsult.current = true;
    agent.call(`sip:${to}@${info?.realm ?? ''}`, callOpts());
  }, [info?.realm, callOpts]);

  const consultComplete = useCallback(() => {
    const main = session.current;
    const other = consultSession.current;
    if (!main || !other) return;
    unmix();
    // The caller is handed to whoever was consulted: REFER with Replaces, done by the switch.
    main.refer(other.remote_identity.uri, {
      replaces: other,
      eventHandlers: {
        // The switch hangs up both legs once it has joined them; this makes sure of it.
        accepted: () => setTimeout(() => { other.isEnded() || other.terminate(); main.isEnded() || main.terminate(); }, 1_500),
        failed: () => setError('The transfer did not go through. You are still on both calls.'),
        requestFailed: () => setError('The transfer was refused. You are still on both calls.'),
      },
    });
  }, [unmix]);

  const consultCancel = useCallback(() => { consultSession.current?.terminate(); }, []);

  const consultSwap = useCallback(() => {
    const main = session.current;
    const other = consultSession.current;
    if (!main || !other || mixer.current) return;
    if (main.isOnHold().local) { other.hold(); main.unhold(); } else { main.hold(); other.unhold(); }
  }, []);

  /**
   * Three on one call, joined here: each side is sent this microphone and the other side, and both
   * are played. No conference room on the switch is needed for three; it costs this computer two
   * calls' worth of audio, which it was already carrying.
   */
  const merge = useCallback(() => {
    const a = session.current;
    const b = consultSession.current;
    if (!a || !b || mixer.current) return;
    if (a.isOnHold().local) a.unhold();
    if (b.isOnHold().local) b.unhold();
    const go = (): void => {
      const micT = audioSender(a)?.track ?? audioSender(b)?.track;
      if (!micT) return;
      const ctx = new AudioContext();
      const gain = ctx.createGain();
      gain.gain.value = call?.muted ? 0 : 1;
      ctx.createMediaStreamSource(new MediaStream([micT])).connect(gain);
      const toA = ctx.createMediaStreamDestination();
      const toB = ctx.createMediaStreamDestination();
      gain.connect(toA); gain.connect(toB);
      ctx.createMediaStreamSource(remoteStream(b)).connect(toA);
      ctx.createMediaStreamSource(remoteStream(a)).connect(toB);
      void audioSender(a)?.replaceTrack(toA.stream.getAudioTracks()[0]!);
      void audioSender(b)?.replaceTrack(toB.stream.getAudioTracks()[0]!);
      mixer.current = { ctx, gain };
      setConference(true);
    };
    // After the unholds have gone through, so the tracks are flowing.
    setTimeout(go, 600);
  }, [call?.muted]);

  // ----------------------------------------------------------------- devices
  const setMic = useCallback((id: string | null) => {
    setMicId(id);
    write('arc.phone.mic', id);
    // Mid-call: the new microphone replaces the old one on the call, without asking the far end.
    if (!session.current || mixer.current) return;
    navigator.mediaDevices.getUserMedia({ audio: id ? { deviceId: { exact: id } } : true }).then((stream) => {
      const t = stream.getAudioTracks()[0]!;
      for (const s of [session.current, consultSession.current]) {
        const sender = audioSender(s);
        if (!sender) continue;
        const old = sender.track;
        t.enabled = !call?.muted;
        void sender.replaceTrack(t).then(() => old?.stop());
      }
    }).catch(() => setError('That microphone could not be opened.'));
  }, [call?.muted]);

  const setSpeaker = useCallback((id: string | null) => { setSpeakerId(id); write('arc.phone.speaker', id); }, []);

  const allowDevices = useCallback(async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
    } catch { setError('The browser was not allowed to use the microphone.'); }
    await listDevices();
  }, [listDevices]);

  const micTrack = useCallback((): MediaStreamTrack | null => {
    if (mixer.current) return null;
    return audioSender(session.current)?.track ?? null;
  }, []);
  const remoteTrack = useCallback((): MediaStreamTrack | null =>
    session.current?.connection?.getReceivers().find((r) => r.track?.kind === 'audio')?.track ?? null, []);

  // This seat's conversations, kept in the browser (messages.ts).
  useEffect(() => { if (extensionId) useMessages.getState().load(extensionId); }, [extensionId]);

  /**
   * A message to a colleague's extension. The edge answers for every device on it at once: 200
   * when one took it, 480 when none of them shows messages (a desk phone only, or nothing
   * signed in), 404 when there is no such extension.
   */
  const sendMessage = useCallback((extension: string, text: string) => {
    const store = useMessages.getState();
    const id = store.send(extension, text);
    const realm = info?.realm;
    if (!ua.current || !realm) { store.settle(extension, id, 'failed'); return; }
    ua.current.sendMessage(`sip:${extension}@${realm}`, text, {
      contentType: 'text/plain;charset=utf-8',
      eventHandlers: {
        succeeded: () => useMessages.getState().settle(extension, id, 'sent'),
        failed: (e: { response?: { status_code?: number } | null }) => {
          const code = e.response?.status_code;
          useMessages.getState().settle(extension, id, code === 480 || code === 404 ? 'unavailable' : 'failed');
        },
      },
    });
  }, [info?.realm]);

  return useMemo(() => ({
    info, state, error, call, lastEnd, elsewhere,
    signIn, signOut, takeOver, dial, answer, hangUp, hold, mute, sendDtmf, transfer,
    consult, conference, consultStart, consultComplete, consultCancel, consultSwap, merge,
    devices, micId, speakerId, setMic, setSpeaker, allowDevices, micTrack, remoteTrack, sendMessage,
  }), [info, state, error, call, lastEnd, elsewhere, signIn, signOut, takeOver, dial, answer, hangUp, hold, mute,
    sendDtmf, transfer, consult, conference, consultStart, consultComplete, consultCancel, consultSwap, merge,
    devices, micId, speakerId, setMic, setSpeaker, allowDevices, micTrack, remoteTrack, sendMessage]);
}
